/**
 * MONEDA-FORMATEADOR.JS — Funciones puras de formateo de moneda
 *
 * Sin estado, sin side effects — solo transforma datos.
 * Soporta valores negativos: -5000 → "-S/ 50.00"
 *
 * Consumido por: moneda-service.js, currency-config.js (wrapper), admin IIFE
 * Compatible: ES Modules (fronts) + CommonJS (backend)
 */

import { PRESETS_MONEDA, MONEDA_POR_DEFECTO, obtenerPresetPorCodigo, unidadesMenoresPorUnidad } from './moneda-presets.js';

// ============================================================
// FORMATEO DE NÚMEROS
// ============================================================

/**
 * Formatea un número con separadores personalizados
 * @param {number} valor - Número a formatear
 * @param {number} decimales - Cantidad de decimales
 * @param {string} separadorDecimal - Carácter decimal (ej: ',' o '.')
 * @param {string} separadorMiles - Carácter de miles (ej: '.' o ',')
 * @returns {string} Número formateado (ej: "1.234,56")
 */
export function formatearNumeroConSeparadores(valor, decimales, separadorDecimal, separadorMiles) {
    const valorFijo = valor.toFixed(decimales);
    const partes = valorFijo.split('.');
    const parteEnteraFormateada = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, separadorMiles);

    if (decimales === 0) return parteEnteraFormateada;
    return parteEnteraFormateada + separadorDecimal + partes[1];
}

// ============================================================
// FORMATEO DE MONEDA
// ============================================================

/**
 * Formatea centavos a moneda con símbolo
 * Soporta valores negativos: -5000 → "-S/ 50.00"
 * @param {number} centavos - Valor en centavos (puede ser negativo)
 * @param {string|object} moneda - Código ISO o preset object. Default: moneda por defecto
 * @returns {string} Ej: "R$ 1.234,56" o "-S/ 50.00"
 */
export function formatearCentavosConSimbolo(centavos, moneda) {
    const preset =
        typeof moneda === 'object' && moneda !== null ? moneda : obtenerPresetPorCodigo(moneda || MONEDA_POR_DEFECTO);

    // Monedas con decimales=0 (PYG, COP) almacenan unidades directas, no centavos
    const divisor = unidadesMenoresPorUnidad(preset);
    const valorBruto = (parseInt(centavos) || 0) / divisor;
    const esNegativo = valorBruto < 0;
    const valorAbsoluto = Math.abs(valorBruto);
    const valorFormateado = formatearNumeroConSeparadores(
        valorAbsoluto,
        preset.decimales,
        preset.separadorDecimal,
        preset.separadorMiles
    );
    const prefijo = esNegativo ? '-' : '';

    if (preset.posicion === 'right') return prefijo + valorFormateado + ' ' + preset.simbolo;
    return prefijo + preset.simbolo + ' ' + valorFormateado;
}

/**
 * Formatea centavos a número sin símbolo
 * @param {number} centavos - Valor en centavos
 * @param {string|object} moneda - Código ISO o preset object
 * @returns {string} Ej: "1.234,56"
 */
export function formatearCentavosSoloValor(centavos, moneda) {
    const preset =
        typeof moneda === 'object' && moneda !== null ? moneda : obtenerPresetPorCodigo(moneda || MONEDA_POR_DEFECTO);

    const divisor = unidadesMenoresPorUnidad(preset);
    return formatearNumeroConSeparadores(
        (parseInt(centavos) || 0) / divisor,
        preset.decimales,
        preset.separadorDecimal,
        preset.separadorMiles
    );
}

/**
 * Formatea unidades (no centavos) a moneda con símbolo
 * @param {number} unidades - Valor en unidades (ej: 50.00, no 5000)
 * @param {string|object} moneda - Código ISO o preset object
 * @returns {string} Ej: "S/ 50.00"
 */
export function formatearUnidadesConSimbolo(unidades, moneda) {
    const preset =
        typeof moneda === 'object' && moneda !== null ? moneda : obtenerPresetPorCodigo(moneda || MONEDA_POR_DEFECTO);

    const valor = parseFloat(unidades) || 0;
    const esNegativo = valor < 0;
    const valorAbsoluto = Math.abs(valor);
    const valorFormateado = formatearNumeroConSeparadores(
        valorAbsoluto,
        preset.decimales,
        preset.separadorDecimal,
        preset.separadorMiles
    );
    const prefijo = esNegativo ? '-' : '';

    if (preset.posicion === 'right') return prefijo + valorFormateado + ' ' + preset.simbolo;
    return prefijo + preset.simbolo + ' ' + valorFormateado;
}

/**
 * Obtiene solo el símbolo de una moneda
 * @param {string} codigoIso - Código ISO
 * @returns {string} Ej: "R$", "S/", "€"
 */
export function obtenerSimbolo(codigoIso) {
    return obtenerPresetPorCodigo(codigoIso).simbolo;
}

// ============================================================
// COMMONJS COMPATIBILITY
// ============================================================

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        formatearNumeroConSeparadores,
        formatearCentavosConSimbolo,
        formatearCentavosSoloValor,
        formatearUnidadesConSimbolo,
        obtenerSimbolo,
    };
}
